import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Send, Phone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCustomer } from "@/lib/auth";

const services = ["Puri Sightseeing", "All-Odisha Tour", "Scooter / Bike Rental", "Car Rental (Local)", "Car Rental (Outstation)", "Drone Aerial Shoot (₹1,000)"];

export default function EnquiryForm() {
  const [form, setForm] = useState({ name: "", phone: "", service: services[0], travel_dates: "", message: "" });
  const [loading, setLoading] = useState(false);
  const { user } = useCustomer();
  useEffect(() => { if (user?.name) setForm((f) => (f.name ? f : { ...f, name: user.name })); }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry received! Our Puri team will call you back shortly.");
      setForm({ name: user?.name || "", phone: "", service: services[0], travel_dates: "", message: "" });
    } catch {
      toast.error("Could not send enquiry. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-white border border-ink/15 rounded-xl px-4 py-3 text-sm text-ink placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-terra/50 focus:border-terra transition";

  return (
    <section id="enquiry" className="py-20 lg:py-28" data-testid="enquiry-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-terra mb-3">10 · Plan Your Trip</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink leading-tight">
            Tell us where the road should take you
          </h2>
          <p className="mt-4 text-ink/70 leading-relaxed">
            Share your dates and what you're dreaming of — darshan, dolphins, or just two wheels and the marine drive. We call back within the hour, every day 7 AM – 10 PM.
          </p>
          <div className="mt-8 flex items-center gap-4 bg-ink text-sand rounded-2xl p-5">
            <span className="w-11 h-11 rounded-full bg-terra flex items-center justify-center shrink-0">
              <Phone size={18} />
            </span>
            <div>
              <p className="text-xs text-sand/60 font-mono uppercase tracking-widest">Prefer to talk?</p>
              <p className="font-serif text-xl font-bold" data-testid="enquiry-phone-number">+91 90400 03444</p>
            </div>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          onSubmit={submit}
          className="lg:col-span-7 bg-surface rounded-3xl p-6 sm:p-10 border border-ink/10"
          data-testid="enquiry-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-ink/60 mb-2">Full Name</label>
              <input required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} data-testid="enquiry-name-input" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-ink/60 mb-2">WhatsApp Number</label>
              <input required value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputCls} data-testid="enquiry-phone-input" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-ink/60 mb-2">I'm interested in</label>
              <select value={form.service} onChange={set("service")} className={inputCls} data-testid="enquiry-service-select">
                {services.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-ink/60 mb-2">Travel Dates</label>
              <input value={form.travel_dates} onChange={set("travel_dates")} placeholder="e.g. 12–15 Aug" className={inputCls} data-testid="enquiry-dates-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-mono uppercase tracking-widest text-ink/60 mb-2">Special Requests</label>
              <textarea rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about your group, pickup point, must-see spots..." className={inputCls} data-testid="enquiry-message-input" />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="enquiry-submit-button"
            className="mt-7 w-full sm:w-auto flex items-center justify-center gap-2 bg-terra text-sand font-semibold px-8 py-3.5 rounded-full hover:bg-ink hover:scale-[1.02] transition-all duration-300 disabled:opacity-60"
          >
            <Send size={16} /> {loading ? "Sending..." : "Send Enquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
